import React from 'react'
import { css } from '@emotion/core'
import { Toggle, Separator } from '@fs/zion-ui'
import { VisuallyHidden } from '@reach/visually-hidden'
import { useTranslation } from 'react-i18next'

const panelCss = css`
  display: flex;
  flex-direction: column;
  padding: 8px 0;
`

// h2/p carry default browser margins that the previous spans did not — zero them out so the
// rendered spacing is unchanged.
const titleCss = css`
  font-weight: bold;
  font-size: 1.25rem;
  margin: 0 0 8px;
`

const descriptionCss = css`
  font-size: 0.875rem;
  margin: 0 0 16px;
  line-height: 1.4;
`

const toggleRowCss = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
`

// A locked toggle is greyed to read as disabled. The switch stays interactive so a tap still fires
// onChange (which the page routes to the "go to system settings" prompt) — the controlled `checked`
// never changes, so it doesn't flip. Greying goes on the wrapper, not the Zion component itself.
const lockedToggleCss = css`
  opacity: 0.4;
`

export default function NotificationEditPanel({ category, onToggle, isChannelLocked = () => false }) {
  const { t } = useTranslation()
  const channels = category.channels ?? []

  // A locked toggle looks greyed but stays interactive, which tells assistive tech nothing. Point every
  // locked toggle at one visually hidden hint (rendered once per panel) explaining why it won't change.
  const lockedHintId = `notification-locked-hint-${category.categoryId}`
  const hasLockedChannel = channels.some((channel) => isChannelLocked(channel.channel))

  return (
    <div css={panelCss}>
      <h2 css={titleCss}>{category.name}</h2>
      <p css={descriptionCss}>{category.description}</p>
      {hasLockedChannel && (
        <VisuallyHidden id={lockedHintId}>
          {t(
            'notifications.locked-toggle-hint',
            'Turn on push notifications from FamilySearch in your device settings to change this.'
          )}
        </VisuallyHidden>
      )}
      {channels.map((channel, index) => {
        const locked = isChannelLocked(channel.channel)
        const toggle = (
          <Toggle
            aria-label={`${channel.name} ${category.name}`}
            aria-describedby={locked ? lockedHintId : undefined}
            checked={channel.subscribed}
            onChange={(e) => onToggle(category.categoryId, channel.channel, e.target.checked)}
          />
        )
        return (
          <React.Fragment key={channel.channel}>
            {index > 0 && <Separator />}
            <div css={toggleRowCss}>
              <span>{channel.name}</span>
              {locked ? <span css={lockedToggleCss}>{toggle}</span> : toggle}
            </div>
          </React.Fragment>
        )
      })}
    </div>
  )
}
